<template>
  <MasterModalBase
    width-xl
    overlay-scrollb
    :navigating="navDirection"
    :action-buttons="actionButtons"
    :main-transition-key="quotationId"
    class="invoice-master-modal"
    @close-modal="close"
  >
    <template v-slot:title>
      <div v-if="!loading">
        <span v-if="quotation">
          {{ $gettext('Quotation') }} {{ quotation.number }}
        </span>
        <span v-if="!quotation">{{ $gettext('New') }} {{ $gettext('Quotation') }}</span>
      </div>
    </template>

    <template v-slot:navbar>

      <!-- Tab: Add -->
      <div class="buttons-wrap" v-if="!quotationIdSet" :class="[(stage === 'create') ? 'active' : '']">
        <div id="tt-invmodal-edit" class="buttons-tt-wrap">
          <GekkoButton
            v-if="!quotationIdSet"
            :text="$gettext('Add')"
            :icon="'fa-regular fa-pen-to-square'"
            :variant="'modal-tab'"
            :class="['px-0', (stage === 'create') ? 'active' : '']"
          />
        </div>
      </div>

      <!-- Tab: Edit -->
      <div class="buttons-wrap" :class="[(stage === 'edit') ? 'active' : '']">
        <div id="tt-invmodal-edit" class="buttons-tt-wrap">
          <GekkoButton
            v-if="quotationIdSet"
            @click.prevent="loadView('edit')"
            :disabled="!quotationIdSet || quotation && quotation.status !== 'draft' && quotation.status !== 'shared' && quotation.status !== 'rejected'"
            :text="$gettext('Edit')"
            :icon="'fa-regular fa-pen-to-square'"
            :variant="'modal-tab'"
            :class="['px-0', (stage === 'edit') ? 'active' : '']"
          />
        </div>
        <b-tooltip v-if="!quotationIdSet || quotation && quotation.status !== 'draft' && quotation.status !== 'shared' && quotation.status !== 'rejected'" target="tt-invmodal-edit" key="tt-invmodal-edit" placement="bottom" triggers="hover">
          <div class="tooltip-text">
            {{ $gettext('Editing is disabled for accepted or invoiced quotations') }}
          </div>
        </b-tooltip>
      </div>

      <!-- Tab: View -->
      <div class="buttons-wrap" :class="[(stage === 'view') ? 'active' : '']">
        <div id="tt-invmodal-view" class="buttons-tt-wrap">
          <GekkoButton
            @click.prevent="loadView('view')"
            :disabled="!quotationIdSet"
            :text="$gettext('View')"
            :icon="'fa-regular fa-eye'"
            :variant="'modal-tab'"
            :class="['px-0', (stage === 'view') ? 'active' : '']"
          />
        </div>
        <b-tooltip v-if="!quotationIdSet" target="tt-invmodal-view" key="tt-invmodal-view" placement="bottom" triggers="hover">
          <div class="tooltip-text">
            {{ $gettext('Quotation is not saved') }}
          </div>
        </b-tooltip>
      </div>
      <div class="buttons-wrap" :class="[(stage === 'send') ? 'active' : '']">
        <div id="tt-invmodal-send" class="buttons-tt-wrap">
          <GekkoButton
            @click.prevent="loadView('send')"
            :disabled="!quotationIdSet"
            :text="$gettext('Send')"
            :icon="'fa-regular fa-paper-plane'"
            :variant="'modal-tab'"
            :class="['px-0', (stage === 'send') ? 'active' : '']"
          />
        </div>
        <b-tooltip v-if="!quotationIdSet" target="tt-invmodal-send" key="tt-invmodal-send" placement="bottom" triggers="hover">
          <div class="tooltip-text">
            {{ $gettext('Quotation is not saved') }}
          </div>
        </b-tooltip>
      </div>
    </template>

    <template v-slot:maincontent>
      <div class="master-invoice-modal-view-wrap">
        <LoadingSpinner v-if="loading" />
        <div v-else class="master-invoice-modal-view">
          <div v-if="stage === 'create'" class="view-wrap" :class="['view-wrap--' + stage]">
            <InvoiceAdd
              is-quotation
              ref="invoice-master-add"
              @display-buttons="showActionButtons"
              @invoice-updated="onQuotationCreated"
            />
          </div>
          <div v-if="stage === 'edit'" class="view-wrap" :class="['view-wrap--' + stage]">
            <InvoiceAdd
              is-edit
              is-quotation
              ref="invoice-master-add"
              @display-buttons="showActionButtons"
              @invoice-updated="onQuotationUpdated"
            />
          </div>
          <div v-if="stage === 'view'" class="view-wrap" :class="['view-wrap--' + stage]">
            <InvoiceView
              is-quotation
              ref="invoice-master-view"
              @display-buttons="showActionButtons"
              @invoice-updated="setListRefresh"
            />
          </div>
          <div v-if="stage === 'send'" class="view-wrap" :class="['view-wrap--' + stage]">
            <InvoiceSend
              is-quotation
              ref="invoice-master-send"
              @display-buttons="showActionButtons"
              @invoice-updated="setListRefresh"
            />
          </div>
        </div>
      </div>
    </template>

    <template v-if="navDirection !== 'no-transition' && quotation && stage === 'view' && actionButtons && actionButtons != ''" v-slot:prevnext>
      <PrevNextNavInvoices is-quotation :item-id="quotationId" @nav-direction="(dir) => { navDirection = dir }" />
    </template>

    <template v-if="actionButtons && actionButtons != ''" v-slot:actions>
      <div class="bottom-action-btns--wrap">

        <!-- VIEW QUOTATION -->
        <template v-if="quotation && actionButtons === 'view'">

          <!-- copy to invoice -->
          <GekkoButton
            v-if="quotation.status === 'accepted'"
            @click="toInvoice()"
            :text="$gettext('Copy to invoice')"
            variant="outline-primary"
            icon="fa-solid fa-arrows-rotate"
          />

          <!-- change status -->
          <GekkoButton 
            v-if="quotation"
            @click="setQuotationToChange(quotation)"
            :text="$gettext('Change status')"
            variant="outline-primary"
            icon="fa-solid fa-arrows-rotate"
          />
          <!-- send -->
          <GekkoButton 
            v-if="quotation"
            :to="{ name: 'QuotationSend', params: { quotationId: quotationId } }"
            :text="$gettext('Send')"
            icon="fa-regular fa-paper-plane"
          />
        </template>

        <!-- SEND QUOTATION -->
        <template v-if="quotation && actionButtons === 'send' && $refs['invoice-master-send']">
          <!-- send quotation -->
          <GekkoButton 
            @click="$refs['invoice-master-send'].adjustAndSend()"
            :disabled="$refs['invoice-master-send'].uploadingFiles || $refs['invoice-master-send'].isSending"
            :text="$gettext('Send')"
            icon="fa-regular fa-paper-plane"
            type="submit"
          />
        </template>

        <!-- ADD/EDIT QUOTATION -->
        <template v-if="actionButtons === 'create'">
          <!-- save draft quotation -->
          <GekkoButton
            v-if="!quotation"
            @click="$refs['invoice-master-add'].saveInvoice()"
            :text="$gettext('Save draft')"
            icon="fa-regular fa-floppy-disk"
            variant="primary"
          />
          <!-- save and send -->
          <GekkoButton 
            v-if="quotation && quotation.status === 'draft'"
            @click="$refs['invoice-master-add'].saveInvoice(true)"
            :text="$gettext('Save & Send quotation')"
            icon="fa-regular fa-paper-plane"
          />
          <!-- save and notify -->
          <GekkoButton 
            v-if="quotation && quotation.status !== 'draft'"
            @click="$refs['invoice-master-add'].saveInvoice(true)"
            :text="$gettext('Update quotation & notify customer')"
            icon="fa-regular fa-paper-plane"
          />
        </template>

        <!-- MORE ACTIONS DROPDOWN -->
        <div v-if="quotation && stage === 'view'">
          <b-dropdown class="icon-toggle ui-dashboard-menu-dropdown" :key="'options-' + quotation.id" variant="outline-primary" right no-caret dropup>
            <template #button-content>
              <font-awesome-icon icon="fa-solid fa-ellipsis-vertical" />
            </template>

            <b-dropdown-item @click="downloadQuotation(quotation.uuid)" :title="$gettext('download invoice')">
              <font-awesome-icon icon="fa-regular fa-download" />
              <span v-translate>Download</span>
            </b-dropdown-item>
           
            <b-dropdown-item v-if="stage === 'view' && quotation.status === 'draft'" :to="{ name: 'QuotationSend', params: { quotationId: quotationId } }">
              <font-awesome-icon icon="fa-regular fa-paper-plane" />
              <span v-translate>Send</span>
            </b-dropdown-item>
            
            <b-dropdown-item v-if="stage !== 'edit' && (quotation.status === 'draft')" :to="{ name: 'QuotationEdit', params: { quotationId: quotationId } }">
              <font-awesome-icon icon="fa-regular fa-pen" />
              <span v-translate>Edit</span>
            </b-dropdown-item>

            <b-dropdown-item v-if="stage === 'view' && quotation.status === 'accepted'" @click="toInvoice()">
              <font-awesome-icon icon="fa-regular fa-copy" />
              <span v-translate>Copy to invoice</span>
            </b-dropdown-item>

            <DropdownItemDelete
              is-quotation
              :key="'dd-delete-' + quotation.id"
              :item="quotation"
              :item-type="'quotation'"
              @item-deleted="onInvoiceDelete"
            />
          </b-dropdown>
        </div>

      </div>
    </template>

  </MasterModalBase>
</template>
  
<script>
import { mapGetters, mapActions } from "vuex"

import { promptDownload } from "@/utils/"

import invoices from "@/mixins/invoices"

import QuotationService from "@/store/services/quotation.service.js"
import InvoiceAdd from '@/components/invoices/InvoiceAdd'
import InvoiceView from '@/components/invoices/InvoiceView'
import InvoiceSend from '@/components/invoices/InvoiceSend'

import GekkoButton from "@/components/GekkoButton"
import MasterModalBase from "@/components/base_components/MasterModalBase"
import LoadingSpinner from "@/components/dashboard/ui/LoadingSpinner"
import DropdownItemDelete from "@/components/ui/DropdownItemDelete"
import StatusBadge from "@/components/invoices/ui/StatusBadge"
import PrevNextNavInvoices from "@/components/invoices/ui/PrevNextNavInvoices"

export default {
  name: 'QuotationMasterModal',
  mixins: [invoices],
  components: {
    MasterModalBase,
    GekkoButton,
    LoadingSpinner,
    InvoiceView,
    InvoiceAdd,
    InvoiceSend,
    DropdownItemDelete,
    StatusBadge,
    PrevNextNavInvoices
  },
  props: {
    stage: {
      type: String,
      default: null
    },
    quotationId: {
      type: [String, Number],
      default: null
    }
  },
  data () {
    return {
      loading: true,
      actionButtons: null,
      refreshList: false,
      navDirection: null
    }
  },
  computed: {
    ...mapGetters({ 
      currentCompany: 'main/getCurrentCompany',
      quotation: 'invoices/getInvoice'
    }),
    quotationIdSet () {
      return (this.quotationId && this.quotationId != '')
    }
  },
  watch: {
    quotationId () {
      this.$nextTick(() => {
        // reset recurrent data because it can be set from outside in RecurrentInvoiceModal
        this.resetQuotationData()
        this.setupData()
      })
    }
  },
  created () {
    // reset recurrent data because it can be set from outside in RecurrentInvoiceModal
    this.setupData()
  },
  destroyed () {
    console.log('master destroyed')
  },
  mounted () {
  },
  methods: {
    ...mapActions({
      loadQuotation: 'invoices/loadQuotation',
      loadQuotationFiles: 'invoices/loadQuotationFiles',
      resetQuotation: 'invoices/resetInvoice',
      resetQuotationData: 'invoices/resetInvoiceData',
      setQuotationToChange: 'invoices/setQuotationToChange',
    }),
    toInvoice(){
      QuotationService.toInvoice(this.quotationId).then((response) => {
        this.$router.push({ name: 'InvoiceView', params: { invoiceId: response.data } }).catch(() => {})
      })
    },
    close () {
      this.resetQuotation()
      this.resetQuotationData()
      if (this.refreshList) {
        this.$store.commit('invoices/REFRESH_INVOICE_LIST')
      }
      this.$emit('close')
      this.$router.push({ name: 'Quotations' }).catch(() => {})
    },
    onInvoiceDelete () {
      this.refreshList = true
      this.close()
    },
    loadView (view) {
      switch (view) {
        case 'create':
          this.$router.push({ name: 'QuotationAdd' }).catch(() => {})
          break;
        case 'edit':
          this.$router.push({ name: 'QuotationEdit', params: { quotationId: this.quotationId } }).catch(() => {})
          break;
        case 'view':
          this.$router.push({ name: 'QuotationView', params: { quotationId: this.quotationId } }).catch(() => {})
          break;
        case 'send':
          this.$router.push({ name: 'QuotationSend', params: { quotationId: this.quotationId } }).catch(() => {})
          break;
      }
    },
    setupData () {
      if (this.stage !== 'create' && this.quotationId) {
        this.loading = true
        this.loadQuotation(this.quotationId).then(() => {
          this.loading = false
        }).catch((error) => {
          console.log(error)
          this.$store.commit('setAlert', {
            title: this.$gettext('Not found'),
            text: this.$gettext('The invoice you are trying to open does not exist'),
            variant: 'warning',
            duration: 4
          })
          this.$router.push({ name: 'Quotations' }).catch(() => {})
        })
      } else {
        this.loading = false
      }
    },
    showActionButtons (payload) {
      this.$nextTick(() => {
        this.actionButtons = payload
      })
    },
    setListRefresh () {
      this.setupData()
      this.refreshList = true
    },
    onQuotationCreated () {
      this.$store.commit('setAlert', {
        title: this.$gettext('Quotation created'),
        text: this.$gettext('The quotation was created successfully'),
        variant: 'primary',
        duration: 4
      })
      this.navDirection = 'no-transition'
      this.setListRefresh()
    },
    onQuotationUpdated () {
      this.$store.commit('setAlert', {
        title: this.$gettext('Quotation updated'),
        text: this.$gettext('The quotation was edited successfully'),
        variant: 'primary',
        duration: 4
      })
      this.setListRefresh()
    },
    downloadQuotation (quotation_id) {
      QuotationService.getQuotationPDFBlob(quotation_id).then(response => {
        promptDownload(response)
      }).catch(error => console.error('Error downloading PDF:', error));
    },
  }
}
</script>

<style lang="scss">

</style>

<style lang="scss" scoped>

</style>